import { useEffect } from "react";
import { useSettings } from "@/lib/settings";
import { captureUtmsOnce, trackConversion } from "@/lib/track";

/**
 * Strict format guards for every tracking ID we inject. Even though these come
 * from an admin-authenticated endpoint, we validate them client-side so a typo
 * or pasted unrelated content can never break out of the URL/script context.
 */
const ID_PATTERNS = {
  ads: /^AW-[A-Z0-9]{6,15}$/i,           // AW-1234567890
  ga4: /^G-[A-Z0-9]{6,15}$/i,            // G-XXXXXXXXXX
  gtm: /^GTM-[A-Z0-9]{4,12}$/i,          // GTM-XXXXXXX
  clarity: /^[a-z0-9]{6,20}$/i,          // alphanumeric project id
  meta: /^\d{8,20}$/,                    // numeric pixel id
};
const isValid = (id, key) => !!id && ID_PATTERNS[key].test(id);

/**
 * Injects analytics scripts conditionally based on Admin → Branding IDs.
 * Empty ID → script not injected. Updates whenever settings change.
 */
export default function TrackingScripts() {
  const settings = useSettings();
  const adsId = isValid(settings?.google_ads_id, "ads") ? settings.google_ads_id : "";
  const ga4Id = isValid(settings?.ga4_measurement_id, "ga4") ? settings.ga4_measurement_id : "";
  const gtmId = isValid(settings?.gtm_container_id, "gtm") ? settings.gtm_container_id : "";
  const clarityId = isValid(settings?.clarity_project_id, "clarity") ? settings.clarity_project_id : "";
  const metaId = isValid(settings?.meta_pixel_id, "meta") ? settings.meta_pixel_id : "";
  const conversionLabels = settings?.conversion_labels || {};

  // Capture UTMs + emit a page_view on every settings load
  useEffect(() => {
    // Publish tracking context so track.js can resolve per-event Ads labels
    if (typeof window !== "undefined") {
      window.__obs_tracking__ = {
        google_ads_id: adsId,
        conversion_labels: conversionLabels,
      };
    }
    captureUtmsOnce();
    trackConversion("page_view", { value: 0 });
  }, [adsId, ga4Id, gtmId, clarityId, metaId, conversionLabels]);

  useEffect(() => {
    if (gtmId) injectGtm(gtmId);
    if (adsId || ga4Id) injectGtag(adsId, ga4Id);
    if (clarityId) injectClarity(clarityId);
    if (metaId) injectMetaPixel(metaId);
  }, [adsId, ga4Id, gtmId, clarityId, metaId]);

  return null;
}

function inject(id, html, attrs = {}) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  if (html) s.text = html;
  document.head.appendChild(s);
}

function injectGtag(adsId, ga4Id) {
  const primary = adsId || ga4Id;
  if (!primary) return;
  inject("gtag-src", null, { async: "true", src: `https://www.googletagmanager.com/gtag/js?id=${primary}` });
  inject("gtag-init", `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    ${adsId ? `gtag('config', '${adsId}');` : ""}
    ${ga4Id ? `gtag('config', '${ga4Id}', { send_page_view: true });` : ""}
  `);
}

function injectGtm(gtmId) {
  inject("gtm-script", `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `);
  if (!document.getElementById("gtm-noscript")) {
    // gtmId is already validated against /^GTM-[A-Z0-9]{4,12}$/i — safe to interpolate
    const ns = document.createElement("noscript");
    ns.id = "gtm-noscript";
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(gtmId)}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    ns.appendChild(iframe);
    document.body.insertBefore(ns, document.body.firstChild);
  }
}

function injectClarity(projectId) {
  inject("clarity-script", `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");
  `);
}

function injectMetaPixel(pixelId) {
  // DEPRECATED site-wide injection — Meta Pixel is now scoped to the
  // /canada-company-india landing family via <MetaPixel/> in those pages.
  // Kept here as a no-op so legacy settings don't accidentally re-enable
  // site-wide tracking.
  // eslint-disable-next-line no-unused-vars
  const _pixelId = pixelId;
}
